import type { PricingTacticsModel } from '@/entities/pricing-campaign'

import React, { Fragment, useMemo } from 'react'
import { find, pipe } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'
import { EngineBadge, PricingEngineType } from '@/entities/pricing-campaign'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	getEnumOptions,
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

import { PricingTacticDemandView } from './PricingTacticDemandView'
import { PricingTacticMarkdownView } from './PricingTacticMarkdownView'
import { PricingTacticPromoView } from './PricingTacticPromoView'
import { PricingTacticRuleBaseView } from './PricingTacticRuleBaseView'

export const PricingTacticsView = ({
	settings,
}: {
	settings: PricingTacticsModel
}) => {
	const { t } = useTranslation()

	const schemaType = useMemo(() => {
		if (settings.engine === PricingEngineType.RB) {
			return 'part:tactics_rb'
		}
		if (settings.engine === PricingEngineType.MD) {
			return 'part:tactics_md'
		}
		if (settings.engine === PricingEngineType.Promo) {
			return 'part:tactics_promo'
		}
		if (settings.engine === PricingEngineType.ML) {
			return 'part:tactics_ml'
		}
	}, [settings.engine])
	const settingsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema(schemaType as string),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (settingsSchema.isLoading) {
		return (
			<Card className='shadow-none'>
				<Skeleton className='h-24 w-full' />
			</Card>
		)
	}
	const schema = settingsSchema.data

	return (
		<Card className='shadow-none'>
			<CardHeader>
				<CardTitle>
					{t('pc.pricing_tactics', { defaultValue: 'Pricing tactics' })}
				</CardTitle>
			</CardHeader>
			<CardContent className='flex flex-col gap-4'>
				{settingsSchema.isLoading ? (
					<PricingTacticsViewSkeleton />
				) : (
					<Fragment>
						<ErrorBoundary>
							<DescriptionList className='flex gap-8'>
								<DescriptionGroup className='flex flex-col'>
									<DescriptionTerm>
										{t('target_price_type.label', {
											defaultValue: 'Price type',
										})}
									</DescriptionTerm>
									<DescriptionDetail className='font-semibold'>
										{
											find(
												{ value: settings?.target_price_type as string },
												getEnumOptions(schema?.properties?.target_price_type)
											)?.label
										}
									</DescriptionDetail>
								</DescriptionGroup>
								<DescriptionGroup className='flex flex-col'>
									<DescriptionTerm>
										{schema?.properties?.engine?.title}
									</DescriptionTerm>
									<DescriptionDetail className='font-semibold'>
										<EngineBadge engine={settings?.engine}>
											{t(settings?.engine, { defaultValue: settings?.engine })}
										</EngineBadge>
									</DescriptionDetail>
								</DescriptionGroup>
							</DescriptionList>
						</ErrorBoundary>
						<Separator />
						<ErrorBoundary>
							{settings.engine === PricingEngineType.RB && (
								<PricingTacticRuleBaseView settings={settings.params} />
							)}
							{settings.engine === PricingEngineType.MD && (
								<PricingTacticMarkdownView settings={settings.params} />
							)}
							{settings.engine === PricingEngineType.Promo && (
								<PricingTacticPromoView settings={settings.params} />
							)}
							{settings.engine === PricingEngineType.ML && (
								<PricingTacticDemandView />
							)}
						</ErrorBoundary>
					</Fragment>
				)}
			</CardContent>
		</Card>
	)
}
const PricingTacticsViewSkeleton = () => (
	<div className='flex flex-col gap-2'>
		<div className='flex gap-2'>
			<Skeleton className='h-6 w-16' />
			<Skeleton className='h-6 w-16' />
		</div>
		<div className='flex gap-2'>
			<Skeleton className='h-6 w-24' />
			<Skeleton className='h-6 w-24' />
		</div>
		<Separator />
		<Skeleton className='h-12 w-full' />
	</div>
)
